import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import {
    fetchWorkerHistory,
    type WorkerAuditEntry,
} from '../../api/workersApi';
import { extractErrorMessageSync } from '../../utils/errorHandler';

export function WorkerAuditTimeline({ workerId }: { workerId: string }) {
    const [entries, setEntries] = useState<WorkerAuditEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(null);
        fetchWorkerHistory(workerId)
            .then((rows) => active && setEntries(rows))
            .catch((reason) => active && setError(extractErrorMessageSync(reason)))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, [workerId]);

    const groups = useMemo(() => groupByDay(entries), [entries]);

    if (loading) return <CircularProgress size={24} aria-label="Loading worker history" />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (entries.length === 0) return <Alert severity="info">No recorded changes.</Alert>;

    return (
        <Stack spacing={3}>
            {groups.map(([day, rows]) => (
                <Box component="section" key={day}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>{day}</Typography>
                    <Stack divider={<Divider flexItem />}>
                        {rows.map((entry) => (
                            <Box key={entry.id} sx={{ py: 1.25 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={0.75} justifyContent="space-between">
                                    <Typography variant="body2" fontWeight={700}>{humanize(entry.field)}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {new Date(entry.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    {entry.metadata?.valueRedacted
                                        ? 'Sensitive value changed'
                                        : `${displayValue(entry.oldValue)} -> ${displayValue(entry.newValue)}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {entry.actorLabel || entry.actorType} · {entry.metadata?.source || 'SYSTEM'}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            ))}
        </Stack>
    );
}

function groupByDay(entries: WorkerAuditEntry[]): Array<[string, WorkerAuditEntry[]]> {
    const grouped = new Map<string, WorkerAuditEntry[]>();
    for (const entry of entries) {
        const day = new Date(entry.createdAt).toLocaleDateString();
        grouped.set(day, [...(grouped.get(day) ?? []), entry]);
    }
    return [...grouped.entries()];
}

function displayValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return 'empty';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
    return JSON.stringify(value);
}

function humanize(value: string): string {
    return value.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/^./, (letter) => letter.toUpperCase());
}
